import {
	ResponsiveContainer,
	LineChart,
	CartesianGrid,
	XAxis,
	YAxis,
	Tooltip as RechartsTooltip,
	Line,
} from "recharts";
import {
	useChartColors,
	useChartTooltipStyle,
} from "../lib/useChartColors";

function formatSeconds(value) {
	const total = Math.max(0, Math.round(Number(value) || 0));
	const mins = Math.floor(total / 60);
	const secs = total % 60;
	return `${mins}:${String(secs).padStart(2, "0")}`;
}

/**
 * Compact progression chart for the exercise history dialog.
 *
 * Mirrors the performance-profile history chart (volt weight line + acid
 * estimated-1RM line for kg, single emerald line otherwise) but draws from the
 * generic series produced by `buildExerciseHistoryChartSeries`, so it lights up
 * for both the athlete diary and the coach feedback console.
 */
export function ExerciseHistoryChart({ series, t }) {
	/* Prima dell'early return: gli hook non possono stare dopo un `return`. */
	const chartColors = useChartColors();
	const tooltipStyle = useChartTooltipStyle();

	if (!series || !Array.isArray(series.points) || series.points.length < 2) {
		return null;
	}

	const { points, mode, unit, hasEstimated1rm } = series;
	const isTime = mode === "time";
	const formatValue = (value) =>
		isTime ? formatSeconds(value) : `${value}${unit ? ` ${unit}` : ""}`;

	const metricLabel =
		mode === "weight"
			? t?.("historyWeight") || "Carico"
			: isTime
				? t?.("timeLabel") || "Tempo"
				: t?.("value") || "Valore";

	return (
		<div className="space-y-2 rounded-sm border border-border bg-background/40 p-2.5">
			<div className="flex flex-wrap items-center gap-3 text-[10px] text-muted-foreground">
				<div className="flex items-center gap-1.5">
					<span className="w-2.5 h-2.5 rounded-full bg-volt" />
					<span>{metricLabel}</span>
				</div>
				{hasEstimated1rm && (
					<div className="flex items-center gap-1.5">
						<span className="w-2.5 h-2.5 rounded-full bg-acid" />
						<span>{t?.("historyEstimated1rm") || "1RM stimato"}</span>
					</div>
				)}
			</div>
			<div className="h-[150px]">
				<ResponsiveContainer width="100%" height="100%">
					<LineChart
						data={points}
						margin={{ top: 4, right: 6, bottom: 0, left: -18 }}
					>
						<CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
						<XAxis
							dataKey="shortDate"
							stroke={chartColors.axis}
							tick={{ fill: chartColors.axis, fontSize: 10 }}
						/>
						<YAxis
							stroke={chartColors.axis}
							tick={{ fill: chartColors.axis, fontSize: 10 }}
							width={44}
							tickFormatter={(value) => (isTime ? formatSeconds(value) : value)}
						/>
						<RechartsTooltip
							contentStyle={tooltipStyle}
							labelStyle={{ color: chartColors.axis }}
							formatter={(value, name, entry) => {
								if (name === "estimated1rm") {
									return [
										formatValue(value),
										t?.("historyEstimated1rm") || "1RM stimato",
									];
								}
								const repsSuffix =
									mode === "weight" && entry?.payload?.reps
										? ` x${entry.payload.reps}`
										: "";
								return [`${formatValue(value)}${repsSuffix}`, metricLabel];
							}}
						/>
						<Line
							type="monotone"
							dataKey="value"
							name="value"
							stroke={mode === "weight" ? chartColors.blue : chartColors.volt}
							strokeWidth={2}
							dot={{
								fill: mode === "weight" ? chartColors.blue : chartColors.volt,
								r: 3,
							}}
						/>
						{hasEstimated1rm && (
							<Line
								type="monotone"
								dataKey="estimated1rm"
								name="estimated1rm"
								stroke={chartColors.acid}
								strokeWidth={2}
								dot={{ fill: chartColors.acid, r: 3 }}
							/>
						)}
					</LineChart>
				</ResponsiveContainer>
			</div>
		</div>
	);
}
